import { browser } from "$app/environment";
import { derivePairId, derivePairKey } from "$crypto/pair-key";
import { b64uDecode, b64uEncode } from "$crypto/encryption";
import { identity } from "$crypto/identity.svelte";

/**
 * VuTwin multi-twin pair store.
 *
 * One device can be bonded with multiple twins. Each pair record holds the
 * friend's X25519 public key, their handle, the time the bond was forged
 * (for the 30-day TTL), and the YMD of the last shared pulse (for the
 * 24-hour per-twin cooldown).
 *
 * The exported singleton stays named `pair` so call sites that read
 * `pair.pairId`, `pair.pairKey`, `pair.friendHandle`, etc. keep working —
 * those getters now resolve through the currently-active pair record.
 *
 * Sender role ('a' or 'b') is determined deterministically by lexicographic
 * comparison of the two base64url-encoded public keys; both sides agree
 * without coordination.
 */

const STORAGE_KEY = "vutwin.pairs.v2";
const LEGACY_KEYS = ["vutwin.pair.v1"];

export const BOND_TTL_DAYS = 30;
const BOND_TTL_SEC = BOND_TTL_DAYS * 86_400;

/** Max simultaneous twin bonds enforced both in the UI and at the store
 *  layer. Keeps the picker drawer scannable and the localStorage payload
 *  bounded. Re-bonding an existing twin always succeeds (it's a refresh,
 *  not a new slot). */
export const MAX_TWINS = 10;

export interface PairRecord {
  pairId: string;
  pubkeyB64: string;
  handle: string;
  /** Unix seconds. */
  bondedAt: number;
  /** UTC YYYY-MM-DD of the last shared pulse with this twin; '' if never. */
  lastPlayedYmd: string;
}

interface StoredPairs {
  records: Record<string, PairRecord>;
  activeId: string;
}

function ymdUtc(d: Date = new Date()): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function nowSec(): number {
  return Math.floor(Date.now() / 1000);
}

class PairsState {
  records = $state<Record<string, PairRecord>>({});
  activeId = $state<string>("");

  private hydrated = false;

  /** All non-expired pairs as an array, newest-first. */
  active = $derived.by<PairRecord[]>(() => {
    const cutoff = nowSec() - BOND_TTL_SEC;
    return Object.values(this.records)
      .filter((r) => r.bondedAt > cutoff)
      .sort((a, b) => b.bondedAt - a.bondedAt);
  });

  /** Currently selected pair (used by Play / Wait / Bond). */
  current = $derived.by<PairRecord | null>(() => {
    if (!this.activeId) return null;
    return this.records[this.activeId] ?? null;
  });

  /** True iff a non-expired pair is selected. */
  isBonded = $derived.by<boolean>(() => {
    const c = this.current;
    if (!c) return false;
    return c.bondedAt > nowSec() - BOND_TTL_SEC;
  });

  /** 32-byte ChaCha20 key for the current pair, or null. */
  pairKey = $derived.by<Uint8Array | null>(() => {
    if (!this.current || !identity.privateKey) return null;
    try {
      const friendPub = b64uDecode(this.current.pubkeyB64);
      return derivePairKey(identity.privateKey, friendPub);
    } catch {
      return null;
    }
  });

  /** Server-routable opaque hex id for the current pair. */
  pairId = $derived.by<string>(() => this.current?.pairId ?? "");

  /** Friend's display handle for the current pair. */
  friendHandle = $derived.by<string>(() => this.current?.handle ?? "");

  /** 'a' if my pubkey sorts before friend's, else 'b'. */
  myRole = $derived.by<"a" | "b">(() => {
    if (!identity.publicKeyB64 || !this.current) return "a";
    return identity.publicKeyB64 < this.current.pubkeyB64 ? "a" : "b";
  });

  friendRole = $derived.by<"a" | "b">(() => (this.myRole === "a" ? "b" : "a"));

  /** Restore from localStorage; drop expired records; migrate v1 if needed. */
  hydrate(): void {
    if (this.hydrated) return;
    this.hydrated = true;
    if (!browser) return;

    // Best-effort migration of the legacy single-pair shape.
    this.maybeMigrateV1();

    for (const k of LEGACY_KEYS) localStorage.removeItem(k);

    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const data = JSON.parse(raw) as Partial<StoredPairs>;
      const incoming = data.records ?? {};
      const cutoff = nowSec() - BOND_TTL_SEC;

      const filtered: Record<string, PairRecord> = {};
      for (const [pid, rec] of Object.entries(incoming)) {
        if (!rec || typeof rec !== "object") continue;
        if (typeof rec.pairId !== "string" || rec.pairId !== pid) continue;
        if (typeof rec.pubkeyB64 !== "string") continue;
        if (rec.bondedAt <= cutoff) continue;
        filtered[pid] = {
          pairId: pid,
          pubkeyB64: rec.pubkeyB64,
          handle: typeof rec.handle === "string" ? rec.handle : "twin",
          bondedAt: rec.bondedAt,
          lastPlayedYmd:
            typeof rec.lastPlayedYmd === "string" ? rec.lastPlayedYmd : "",
        };
      }
      this.records = filtered;

      const desired =
        data.activeId && filtered[data.activeId] ? data.activeId : "";
      this.activeId = desired || this.firstActiveId();

      this.persist();
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  /** Add (or refresh) a bond and make it the active twin. */
  bond(friendPubKeyB64: string, friendHandle: string): string {
    let pk: Uint8Array;
    try {
      pk = b64uDecode(friendPubKeyB64.trim());
    } catch {
      return "";
    }
    if (pk.length !== 32) return "";
    if (!identity.privateKey || !identity.publicKey) return "";
    if (b64uEncode(pk) === b64uEncode(identity.publicKey)) return "";

    const pubkeyB64 = b64uEncode(pk);
    const pairKey = derivePairKey(identity.privateKey, pk);
    const id = derivePairId(pairKey);

    const handle = (friendHandle || "").trim().slice(0, 24) || "twin";
    const existing = this.records[id];
    // Enforce the cap on net-new bonds only — refreshing an existing twin
    // (e.g. someone re-shares their code after a handle change) must always
    // succeed even at the limit.
    if (!existing && this.active.length >= MAX_TWINS) return "";
    const next: PairRecord = {
      pairId: id,
      pubkeyB64,
      handle,
      bondedAt: nowSec(),
      lastPlayedYmd: existing?.lastPlayedYmd ?? "",
    };
    this.records = { ...this.records, [id]: next };
    this.activeId = id;
    this.persist();
    return id;
  }

  /** Switch the currently-selected twin. No-op if pairId unknown. */
  setActive(pairId: string): void {
    if (!this.records[pairId]) return;
    this.activeId = pairId;
    this.persist();
  }

  /** Drop a single bond. Re-selects the next available twin if active. */
  unbond(pairId?: string): void {
    const target = pairId ?? this.activeId;
    if (!target || !this.records[target]) return;
    const next = { ...this.records };
    delete next[target];
    this.records = next;
    if (this.activeId === target) this.activeId = this.firstActiveId();
    this.persist();
  }

  /** Drop every bond. */
  unbondAll(): void {
    this.records = {};
    this.activeId = "";
    this.persist();
  }

  /** Mark a shared pulse as completed for cooldown bookkeeping. */
  recordPulse(pairId: string): void {
    const rec = this.records[pairId];
    if (!rec) return;
    const next: PairRecord = { ...rec, lastPlayedYmd: ymdUtc() };
    this.records = { ...this.records, [pairId]: next };
    this.persist();
  }

  /**
   * Milliseconds until the next allowed pulse with this twin.
   * Returns 0 when ready, otherwise time until 00:00 UTC of the next day
   * after `lastPlayedYmd` (matches the daily-pulse rhythm).
   */
  cooldownRemainingMs(pairId: string): number {
    const rec = this.records[pairId];
    if (!rec || !rec.lastPlayedYmd) return 0;
    if (rec.lastPlayedYmd !== ymdUtc()) return 0;
    const now = new Date();
    const tomorrow = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + 1,
      0,
      0,
      0,
      0,
    );
    return Math.max(0, tomorrow - now.getTime());
  }

  /** Truthy iff this twin has been played-with today. */
  onCooldown(pairId: string): boolean {
    return this.cooldownRemainingMs(pairId) > 0;
  }

  // ------------------------------------------------------------------ private

  private firstActiveId(): string {
    return this.active[0]?.pairId ?? "";
  }

  private persist(): void {
    if (!browser) return;
    const payload: StoredPairs = {
      records: this.records,
      activeId: this.activeId,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // localStorage full / disabled — silent
    }
  }

  /** Pick up an existing v1 single-pair record so first-time multi-pair
   *  upgrades don't lose the existing bond. */
  private maybeMigrateV1(): void {
    const raw = localStorage.getItem("vutwin.pair.v1");
    if (!raw) return;
    try {
      const data = JSON.parse(raw) as {
        friendPubKeyB64?: string;
        friendHandle?: string;
      };
      if (!data.friendPubKeyB64 || !identity.privateKey) return;
      const pk = b64uDecode(data.friendPubKeyB64);
      if (pk.length !== 32) return;
      const pairKey = derivePairKey(identity.privateKey, pk);
      const id = derivePairId(pairKey);
      const rec: PairRecord = {
        pairId: id,
        pubkeyB64: data.friendPubKeyB64,
        handle: (data.friendHandle || "").slice(0, 24) || "twin",
        bondedAt: nowSec(),
        lastPlayedYmd: "",
      };
      this.records = { [id]: rec };
      this.activeId = id;
      this.persist();
    } catch {
      // ignore — v1 entry will be removed below
    }
  }
}

export const pair = new PairsState();

/** Format `Xh Ym` for cooldown badges. */
export function fmtCooldown(ms: number): string {
  if (ms <= 0) return "";
  const totalMin = Math.ceil(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
